// api/appVersion.js
import { ref } from 'vue'
import axios from '@/api'
import { getPlatform, detectAndroidStore, initAndroidStore } from '@/utils/device'

/**
 * 获取当前APP版本号
 * 从构建时生成的常量文件读取（支持平台特定版本）
 */
export const getCurrentVersion = async () => {
  // 动态导入，避免构建时的循环依赖问题
  const { IOS_VERSION, ANDROID_VERSION } = await import('@/version')

  // 根据平台返回对应版本号
  const platform = getPlatform()
  const version = platform === 'ios' ? IOS_VERSION : ANDROID_VERSION

  console.log(`📱 当前APP版本 (${platform}):`, version)
  return version
}

/**
 * 检查APP版本更新
 * @param {string} appType - 端类型：customer-客户端, manage-管理版
 * @param {string} platform - 平台：ios/android
 * @param {string} currentVersion - 当前版本号
 * @param {string} store - 应用商店：huawei/xiaomi/oppo/vivo/tencent/other（仅Android需要）
 */
export const checkVersion = (appType, platform, currentVersion, store = null) => {
  const params = { appType, platform, currentVersion }

  // Android 平台传递应用商店参数
  if (platform === 'android' && store) {
    params.store = store
  }

  return axios.get('/app/version/check', {
    params
  }).then(res => res.data)
}

/**
 * 一键检查版本更新（自动获取当前版本、平台和应用商店）
 * @param {string} appType - 端类型：customer-客户端, manage-管理版
 */
export const checkVersionAuto = async (appType = 'coach') => {
  const platform = getPlatform()
  const currentVersion = await getCurrentVersion()

  // Android 平台增加应用商店检测（原生安装来源优先，UA 兜底）
  let store = null
  if (platform === 'android') {
    await initAndroidStore()
    store = detectAndroidStore()
    console.log('📱 Android 应用商店:', store)
  }

  return checkVersion(appType, platform, currentVersion, store)
}

/**
 * 获取社交功能开关状态
 * iOS 默认开启，Android 根据服务器配置
 */
export const getSocialOn = () => {
  // iOS 默认开启
  if (getPlatform() === 'ios') {
    return true
  }
  // Android 从 localStorage 读取服务器配置
  const socialOn = localStorage.getItem('social_on')
  return socialOn === null ? true : socialOn === 'true'
}

/**
 * 设置社交功能开关状态（仅供版本检查后调用）
 */
export const setSocialOn = (value) => {
  localStorage.setItem('social_on', String(value))
  socialOnState.value = isSocialEnabled()
}

/**
 * 检查社交功能是否可用
 * iOS 直接返回 true，Android 根据 socialOn 配置
 */
export const isSocialEnabled = () => {
  return getPlatform() === 'ios' || getSocialOn()
}

/**
 * 社交功能开关响应式状态
 * 初值取 isSocialEnabled()，版本检测返回后随 setSocialOn 自动更新，
 * 供 TabBar 等组件响应式消费（避免启动时读一次就固定）
 */
export const socialOnState = ref(isSocialEnabled())
